import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaSpinner } from 'react-icons/fa';
import Particles from 'react-tsparticles';
import { loadFull } from 'tsparticles';
import { useKidsFlasher } from '../context/KidsFlasherContext';
import Logo from '../components/Logo';
import { particlesConfig } from '../config/particles';

const HomePage = () => {
  const [riotId, setRiotId] = useState('');
  const [isInputError, setIsInputError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { validateRiotId } = useKidsFlasher();
  const navigate = useNavigate();
  
  const particlesInit = useCallback(async (main) => {
    await loadFull(main);
  }, []);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateRiotId(riotId)) {
      setIsInputError(true);
      return;
    }
    
    setIsInputError(false);
    setIsLoading(true);
    
    navigate(`/loading?riotId=${encodeURIComponent(riotId)}`);
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-700 flex flex-col items-center justify-center relative overflow-hidden px-4 sm:px-6">
      <Particles 
        id="tsparticles" 
        init={particlesInit} 
        options={particlesConfig} 
        className="absolute inset-0" 
      />
      
      <div className="z-10 w-[90%] sm:w-full max-w-sm sm:max-w-md">
        <motion.div 
          className="flex flex-col items-center"
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.2 }}
        >
          <Logo />
          
          <motion.h2 
            className="text-xl md:text-2xl text-blue-300 font-bold mt-4 mb-8"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            Discover the Next Abuser
          </motion.h2>
        </motion.div>
        
        <motion.form 
          className="bg-black bg-opacity-40 backdrop-filter backdrop-blur-lg p-4 sm:p-6 rounded-xl shadow-2xl mx-auto"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.2, duration: 0.5 }}
          onSubmit={handleSubmit}
        >
          <div className="relative">
            <input 
              type="text" 
              value={riotId}
              onChange={(e) => setRiotId(e.target.value)}
              placeholder="Enter Riot ID (username#tag)"
              className={`w-full py-3 px-4 rounded-lg bg-slate-800 bg-opacity-60 border-2 ${
                isInputError ? 'border-red-500' : 'border-blue-500'
              } text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300`}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleSubmit(e);
                }
              }}
            />
            
            {isInputError && (
              <motion.p 
                className="text-red-400 text-sm mt-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                Invalid Riot ID format. Use Username#Tag format.
              </motion.p>
            )}
          </div>
          
          <motion.button
            type="submit"
            disabled={isLoading}
            className="w-full mt-4 py-3 px-6 rounded-lg bg-gradient-to-r from-blue-600 to-blue-800 text-white font-bold flex items-center justify-center gap-2 hover:from-blue-500 hover:to-blue-700 transition duration-300 shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: isLoading ? 1 : 1.03 }}
            whileTap={{ scale: isLoading ? 1 : 0.97 }}
          >
            {isLoading ? (
              <FaSpinner className="animate-spin" />
            ) : (
              <>
                <FaSearch />
                <span>Search Player</span>
              </>
            )}
          </motion.button>
        </motion.form>
      </div>
      
      <motion.footer 
        className="absolute bottom-4 text-center text-blue-300 text-sm w-full"
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.7 }}
        transition={{ delay: 1.8, duration: 0.8 }}
      >
        <div className="flex justify-center space-x-6 mb-2">
          <a href="https://x.com/srkingsz" target="_" className="hover:text-blue-400 transition duration-300">Twitter</a>
          <a href="http://discord.com/kingsz" target="_" className="hover:text-blue-400 transition duration-300">Discord</a>
          <a href="https://github.com/SrKing-9812" target="_" className="hover:text-blue-400 transition duration-300">GitHub</a>
        </div>
        <p className="text-center">© 2025 DelF • Not affiliated with Riot Games</p>
      </motion.footer>
    </div>
  );
};

export default HomePage;